import React from 'react';
import { useTheme } from '@/context/theme.context';
import { Text } from '../atoms/AppText';

interface StatChipProps {
  /** Small icon inside the gradient badge */
  icon: React.ReactNode;
  /** Micro-label rendered above the value */
  label: string;
  /** Main value string */
  value: string;
  /**
   * Ghost mode — no wrapper background/border and no icon badge box.
   * The icon renders as a plain colored symbol sized to the text.
   */
  /**
   * Ghost mode — strips the gradient badge box from the icon, rendering it
   * as a plain colored symbol. The wrapper bg/border/rounded are kept.
   */
  ghost?: boolean;
  /** Extra Tailwind classes on the chip wrapper */
  className?: string;
  /** data-testid for automation */
  testId?: string;
}

/**
 * Compact info chip: a small gradient icon badge on the left + label / value
 * stack on the right.  Used in page headers to surface quick-reference stats.
 *
 * Pass `ghost` to strip the wrapper background/border while keeping the
 * gradient icon badge — ideal for inline header contexts.
 */
export const StatChip = ({
  icon,
  label,
  value,
  ghost = false,
  className = '',
  testId,
}: StatChipProps) => {
  const { colors } = useTheme();

  return (
    <div
      data-testid={testId ?? ''}
      className={[
        'flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/60 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.07]',
        className,
      ].join(' ')}
    >
      {ghost ? (
        /* ── Ghost: plain colored icon, no gradient badge box ── */
        <span className="flex-shrink-0 text-[var(--color-primary)]">
          {icon}
        </span>
      ) : (
        /* ── Default: gradient icon badge ── */
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center text-white flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            boxShadow: `0 2px 6px ${colors.primary}44`,
          }}
        >
          {icon}
        </div>
      )}

      <div className="min-w-0">
        <Text
          as="p"
          size="xs"
          weight="bold"
          color="muted"
          tracking="widest"
          leading="none"
          className="uppercase"
        >
          {label}
        </Text>
        <Text
          as="p"
          size="md"
          weight="semibold"
          color="soft"
          leading="snug"
          className="mt-0.5 truncate"
        >
          {value}
        </Text>
      </div>
    </div>
  );
};
